/**
 * OpenClawUninstaller — Desinstala OpenClaw da máquina remota
 * Inline card (mesmo padrão do Installer)
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button } from './ui';

interface UninstallStep {
  id: string;
  label: string;
  command: string;
  status: 'waiting' | 'running' | 'done' | 'error' | 'skipped';
  output: string;
  exitCode?: number;
  validator?: (output: string, exitCode: number) => { ok: boolean; skip?: boolean; message?: string };
}

interface Props {
  runnerId: string;
  isOnline: boolean;
  runnerOs?: string;
  openclawPath?: string;
  onClose: () => void;
  onUninstalled: () => void;
  onBusyChange?: (busy: boolean) => void;
}

function getPathPrefix(runnerOs: string): string {
  if (runnerOs === 'macos') {
    return 'export PATH="/opt/homebrew/bin:/opt/homebrew/sbin:/usr/local/bin:/usr/local/sbin:$HOME/.nvm/versions/node/*/bin:$HOME/.volta/bin:$PATH"; ';
  }
  return 'export PATH="/usr/local/bin:/usr/bin:$HOME/.nvm/versions/node/*/bin:$HOME/.volta/bin:$HOME/.bun/bin:$PATH"; ';
}

function createSteps(runnerOs: string): UninstallStep[] {
  const P = getPathPrefix(runnerOs);
  return [
    {
      id: 'stop-gateway',
      label: 'Parando OpenClaw Gateway',
      command: P + '(openclaw gateway stop 2>&1 || true) && (pkill -f "openclaw" 2>/dev/null || true) && echo "STOPPED"',
      status: 'waiting',
      output: '',
      validator: (output) => {
        return { ok: true, message: 'Gateway parado ' };
      },
    },
    {
      id: 'uninstall-npm',
      label: 'Desinstalando OpenClaw (npm)',
      command: P + 'npm uninstall -g openclaw 2>&1',
      status: 'waiting',
      output: '',
      validator: (output, exitCode) => {
        if (exitCode !== 0 && !output.includes('removed')) {
          if (output.includes('EACCES') || output.includes('permission denied')) {
            return { ok: false, message: 'Permissão negada. Rode no Terminal: sudo npm uninstall -g openclaw' };
          }
          return { ok: false, message: `Erro ao desinstalar: ${output.slice(-200)}` };
        }
        return { ok: true };
      },
    },
    {
      id: 'cleanup-config',
      label: 'Limpando configurações',
      command: P + 'rm -rf ~/.openclaw 2>&1 && echo "CONFIG_CLEANED"',
      status: 'waiting',
      output: '',
      validator: (output) => {
        return { ok: true, message: 'Configurações removidas ' };
      },
    },
    {
      id: 'verify-removal',
      label: 'Verificando remoção',
      command: P + 'which openclaw 2>&1 || echo "NOT_FOUND"',
      status: 'waiting',
      output: '',
      validator: (output, exitCode) => {
        if (output.includes('NOT_FOUND') || exitCode !== 0) {
 return { ok: true, message: 'OpenClaw completamente removido!' };
        }
        return { ok: false, message: `OpenClaw ainda encontrado em: ${output.trim()}` };
      },
    },
  ];
}

const POLL_MS = 800;

export function OpenClawUninstaller({ runnerId, isOnline, runnerOs = 'linux', onClose, onUninstalled, onBusyChange }: Props) {
  const [phase, setPhase] = useState<'confirm' | 'uninstalling' | 'done' | 'error'>('confirm');
  const [steps, setSteps] = useState<UninstallStep[]>(() => createSteps(runnerOs));
  const [currentStep, setCurrentStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const abortRef = useRef(false);
  const pollRef = useRef<any>(null);

  const totalSteps = steps.length;
  const doneCount = steps.filter(s => s.status === 'done' || s.status === 'skipped').length;
  const progress = phase === 'done' ? 100 : Math.round((doneCount / totalSteps) * 100);

  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const execCommand = useCallback(async (command: string): Promise<{ output: string; exitCode: number; error?: string }> => {
    try {
      const res = await api.post(`/runner-v3/${runnerId}/terminal/exec`, { command }) as any;
      if (!res.success || !res.data?.id) {
        return { output: '', exitCode: -1, error: 'Falha ao enviar comando' };
      }
      const cmdId = res.data.id;

      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 150; // ~2min
        let lastOutput = '';
        let stableCount = 0; // count how many polls output didn't change
        let polling = false;

        pollRef.current = setInterval(async () => {
          if (polling) return; // prevent overlapping async calls
          polling = true;
          attempts++;

          if (abortRef.current) {
            clearInterval(pollRef.current);
            resolve({ output: '', exitCode: -1, error: 'Cancelado' });
            polling = false;
            return;
          }
          try {
            const cmd = await api.get(`/runner-v3/${runnerId}/terminal/output/${cmdId}`) as any;
            if (cmd) {
              if (cmd.status === 'completed' || cmd.status === 'error') {
                clearInterval(pollRef.current);
                resolve({ output: cmd.output || '', exitCode: cmd.exit_code ?? (cmd.status === 'error' ? 1 : 0) });
                polling = false;
                return;
              }
              // Update output in real-time
              if (cmd.output) {
                setSteps(prev => prev.map((s, i) => i === currentStep ? { ...s, output: cmd.output } : s));
                // Detect stale "running" — if output stopped changing for 15+ polls (~12s), treat as done
                if (cmd.output === lastOutput) {
                  stableCount++;
                  if (stableCount >= 15 && cmd.output.length > 0) {
                    clearInterval(pollRef.current);
                    resolve({ output: cmd.output, exitCode: 0 });
                    polling = false;
                    return;
                  }
                } else {
                  stableCount = 0;
                  lastOutput = cmd.output;
                }
              }
            }
          } catch {}
          if (attempts >= maxAttempts) {
            clearInterval(pollRef.current);
            resolve({ output: lastOutput || '', exitCode: -1, error: 'Timeout' });
          }
          polling = false;
        }, POLL_MS);
      });
    } catch (e: any) {
      return { output: '', exitCode: -1, error: e?.message || 'Erro' };
    }
  }, [runnerId, currentStep]);

  const startUninstall = useCallback(async () => {
    setPhase('uninstalling');
    onBusyChange?.(true);
    abortRef.current = false;
    const stepsLocal = createSteps(runnerOs);
    setSteps(stepsLocal);

    for (let i = 0; i < stepsLocal.length; i++) {
      if (abortRef.current) break;
      const step = stepsLocal[i]!;
      setCurrentStep(i);
      step.status = 'running';
      setSteps([...stepsLocal]);

      const result = await execCommand(step.command);

      if (result.error) {
        step.status = 'error';
        step.output = result.error;
        setSteps([...stepsLocal]);
        setErrorMessage(result.error);
        setPhase('error');
        return;
      }

      step.output = result.output;
      step.exitCode = result.exitCode;

      if (step.validator) {
        const v = step.validator(result.output, result.exitCode);
        if (!v.ok) {
          step.status = 'error';
          setSteps([...stepsLocal]);
          setErrorMessage(v.message || 'Falha');
          setPhase('error');
          return;
        }
        if (v.skip) { step.status = 'skipped'; setSteps([...stepsLocal]); continue; }
      }

      step.status = 'done';
      setSteps([...stepsLocal]);
    }

    if (!abortRef.current) {
      setPhase('done');
      onUninstalled();
    }
    onBusyChange?.(false);
  }, [execCommand, onUninstalled]);

  const handleCancel = () => {
    abortRef.current = true;
    if (pollRef.current) clearInterval(pollRef.current);
    onClose();
  };

  // ── Confirm phase ──
  if (phase === 'confirm') {
    return (
      <div style={{
        marginBottom: 20, borderRadius: 14, overflow: 'hidden',
        border: '1px solid rgba(248,81,73,0.3)', background: 'var(--bg-card-solid)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          background: T.dangerBg,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 16 }}></span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Desinstalar OpenClaw</span>
          </div>
          <Button variant="ghost" onClick={handleCancel} style={{
            background: 'none', border: 'none', color: 'var(--text-dim)',
            fontSize: 16, cursor: 'pointer', padding: '2px 4px',
          }}></Button>
        </div>

        <div style={{ padding: '16px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.6 }}>
            Isso vai <strong style={{ color: 'var(--danger)' }}>remover completamente</strong> o OpenClaw desta máquina:
          </p>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            {createSteps(runnerOs).map((s, i) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ color: 'var(--danger)' }}>•</span> {s.label}
              </div>
            ))}
          </div>

          <div style={{
            padding: '8px 12px', borderRadius: 8, marginBottom: 16,
            background: T.dangerBg, border: '1px solid rgba(248,81,73,0.15)',
            fontSize: 11, color: 'var(--danger)',
          }}>
 Configurações em ~/.openclaw serão apagadas. Esta ação não pode ser desfeita.
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={handleCancel} style={btnSecondaryStyle}>Cancelar</Button>
            <Button variant="ghost" onClick={startUninstall} disabled={!isOnline} style={{
              ...btnDangerStyle,
              opacity: isOnline ? 1 : 0.5,
              cursor: isOnline ? 'pointer' : 'not-allowed',
            }}>
 Confirmar Desinstalação
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Progress / Done / Error ──
  return (
    <div style={{
      marginBottom: 20, borderRadius: 14, overflow: 'hidden',
      border: phase === 'done' ? '1px solid rgba(46,204,113,0.3)'
        : phase === 'error' ? '1px solid rgba(248,81,73,0.3)'
        : '1px solid rgba(248,81,73,0.2)',
      background: 'var(--bg-card-solid)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px',
        background: phase === 'done' ? T.successBg : T.dangerBg,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 16 }}>{phase === 'done' ? '' : ''}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
            {phase === 'done' ? 'OpenClaw Removido!' : phase === 'error' ? 'Erro na Desinstalação' : 'Desinstalando...'}
          </span>
          {phase === 'uninstalling' && (
            <span style={{
              fontSize: 9, padding: '1px 6px', borderRadius: 8,
              background: T.dangerBg, color: 'var(--danger)',
              fontWeight: 600, animation: 'pulse 1.5s infinite',
            }}>REMOVENDO</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            fontSize: 13, fontWeight: 700, fontFamily: 'monospace',
            color: phase === 'error' ? 'var(--danger)' : phase === 'done' ? 'var(--success)' : 'var(--danger)',
          }}>{progress}%</span>
          {phase !== 'uninstalling' && (
            <Button variant="ghost" onClick={handleCancel} style={{
              background: 'none', border: 'none', color: 'var(--text-dim)',
              fontSize: 16, cursor: 'pointer', padding: '2px 4px',
            }}></Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ width: '100%', height: 3, background: T.bgCard }}>
        <div style={{
          width: `${progress}%`, height: '100%',
          background: phase === 'done' ? 'var(--success)' : 'var(--danger)',
          transition: 'width 0.4s ease',
        }} />
      </div>

      {/* Steps */}
      <div style={{
        padding: '12px 16px',
        fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
        maxHeight: 300, overflowY: 'auto',
      }}>
        {steps.map((step, i) => (
          <div key={step.id} style={{ marginBottom: i < steps.length - 1 ? 12 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <StepIcon status={step.status} />
              <span style={{
                fontSize: 12, fontWeight: 600,
                color: step.status === 'running' ? 'var(--danger)'
                  : step.status === 'done' ? 'var(--success)'
                  : step.status === 'error' ? 'var(--danger)'
                  : 'var(--text-muted)',
              }}>{step.label}</span>
              {step.status === 'running' && (
                <span style={{
                  fontSize: 9, padding: '1px 6px', borderRadius: 8,
                  background: T.dangerBg, color: 'var(--danger)',
                  fontWeight: 600, animation: 'pulse 1.5s infinite',
                }}>EXECUTANDO</span>
              )}
            </div>
            {step.output && (step.status === 'running' || step.status === 'done' || step.status === 'error') && (
              <div style={{
                marginLeft: 28, fontSize: 10, lineHeight: 1.4,
                color: step.status === 'error' ? 'var(--danger)' : 'var(--text-muted)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 80, overflowY: 'auto',
                padding: '4px 8px', borderRadius: 4,
                background: T.bgSubtle,
                borderLeft: '2px solid #21262d',
              }}>{step.output.slice(-1000)}</div>
            )}
          </div>
        ))}
      </div>

      {/* Error */}
      {errorMessage && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{
            padding: '10px 12px', borderRadius: 8,
            background: T.dangerBg, border: '1px solid rgba(248,81,73,0.15)',
            fontSize: 12, color: 'var(--danger)',
 }}> {errorMessage}</div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        display: 'flex', gap: 8, justifyContent: 'flex-end',
        padding: '8px 16px 12px',
        borderTop: '1px solid rgba(255,255,255,0.04)',
      }}>
        {phase === 'uninstalling' && (
          <Button variant="ghost" onClick={handleCancel} style={btnSecondaryStyle}>Cancelar</Button>
        )}
        {phase === 'error' && (
          <>
            <Button variant="ghost" onClick={handleCancel} style={btnSecondaryStyle}>Fechar</Button>
 <Button variant="ghost" onClick={() => { setErrorMessage(''); startUninstall(); }} style={btnDangerStyle}>Tentar Novamente</Button>
          </>
        )}
        {phase === 'done' && (
 <Button variant="ghost" onClick={handleCancel} style={btnSuccessStyle}>Concluído</Button>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function StepIcon({ status }: { status: UninstallStep['status'] }) {
  const size = 18;
  const base: any = {
    width: size, height: size, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 10, fontWeight: 700, flexShrink: 0,
  };
  if (status === 'done') return <div style={{ ...base, background: T.successBg, color: 'var(--success)' }}></div>;
  if (status === 'error') return <div style={{ ...base, background: T.dangerBg, color: 'var(--danger)' }}></div>;
  if (status === 'skipped') return <div style={{ ...base, background: T.borderHover, color: 'var(--text-muted)' }}>—</div>;
  if (status === 'running') return (
    <div style={{ ...base, border: '2px solid var(--danger)', background: 'transparent' }}>
      <div style={{ width: 8, height: 8, border: '2px solid transparent', borderTopColor: 'var(--danger)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
  return <div style={{ ...base, background: T.borderHover, color: 'var(--text-muted)' }}>○</div>;
}

const btnSecondaryStyle: any = {
  padding: '6px 16px', background: T.border,
  color: 'var(--text-dim)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};
const btnDangerStyle: any = {
  padding: '6px 16px', background: T.dangerBg,
  color: 'var(--danger)', border: '1px solid rgba(248,81,73,0.3)',
  borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};
const btnSuccessStyle: any = {
  padding: '6px 16px', background: T.successBg,
  color: 'var(--success)', border: '1px solid rgba(46,204,113,0.2)',
  borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};
